<template>
  <div class="min-h-screen flex-1 mt-1 bg-gray-100 px-4 pt-8 shadow-lg">
    <div class="bg-white w-full rounded-lg shadow">
      <!-- Header -->
      <div class="h-20 py-3 flex items-center border-gray-200 p-6">
        <div class="flex w-full justify-between">
          <div class="flex items-center">
            <button
              :class="$colors.primary"
              class="rounded-full flex justify-center items-center w-8 h-8 mr-3"
              @click="$router.back()"
            >
              <i class="material-icons">chevron_left</i>
            </button>
            <div class="text-xl font-bold text-green-600">
              Gestion des Variantes - {{ product.name }}
            </div>
          </div>
          <div v-if="product.hasVariants" class="text-sm text-gray-500">
            {{ product.variants ? product.variants.length : 0 }} variante(s)
          </div>
        </div>
      </div>

      <loading :active.sync="loading" :is-full-page="false"></loading>

      <!-- Contenu principal -->
      <div class="p-6">
        <!-- Message si pas de variantes -->
        <div v-if="!product.hasVariants || !product.variants || product.variants.length === 0"
             class="text-center py-12 bg-gray-50 rounded-lg">
          <i class="material-icons text-6xl text-gray-300">inventory_2</i>
          <p class="mt-4 text-gray-500 text-lg">Ce produit n'a pas de variantes</p>
          <p class="text-gray-400 text-sm mt-2">Editez le produit pour ajouter des variantes</p>
          <router-link
            :to="`/products/edit/${$route.params.id}`"
            class="mt-4 inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <i class="material-icons mr-2">edit</i>
            Modifier le produit
          </router-link>
        </div>

        <!-- Tableau des variantes -->
        <div v-else>
          <!-- Filtres -->
          <div class="mb-6 flex flex-wrap gap-4 items-center">
            <div class="flex-1 min-w-64">
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Rechercher par SKU ou attribut..."
                class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-green-500"
              />
            </div>
            <div class="w-48">
              <v-select
                v-model="warehouseFilter"
                :options="warehouses"
                label="name"
                placeholder="Entrepot..."
                @input="filterByWarehouse"
              >
                <template slot="option" slot-scope="option">
                  <img class="flag-img inline mr-2" :src="$f.getCountryCode(option.country)" />
                  {{ option.name }}
                </template>
              </v-select>
            </div>
            <div class="w-40">
              <select v-model="stockFilter" class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green-500">
                <option value="">Tous</option>
                <option value="inStock">En stock</option>
                <option value="lowStock">Stock faible</option>
                <option value="outOfStock">Rupture</option>
              </select>
            </div>
          </div>

          <!-- Résumé du stock -->
          <div class="grid grid-cols-4 gap-4 mb-6">
            <div class="bg-green-50 p-4 rounded-lg">
              <div class="text-2xl font-bold text-green-600">{{ totalInStock }}</div>
              <div class="text-sm text-gray-600">Total en stock</div>
            </div>
            <div class="bg-blue-50 p-4 rounded-lg">
              <div class="text-2xl font-bold text-blue-600">{{ totalVariants }}</div>
              <div class="text-sm text-gray-600">Variantes</div>
            </div>
            <div class="bg-yellow-50 p-4 rounded-lg">
              <div class="text-2xl font-bold text-yellow-600">{{ lowStockCount }}</div>
              <div class="text-sm text-gray-600">Stock faible</div>
            </div>
            <div class="bg-red-50 p-4 rounded-lg">
              <div class="text-2xl font-bold text-red-600">{{ outOfStockCount }}</div>
              <div class="text-sm text-gray-600">Rupture de stock</div>
            </div>
          </div>

          <!-- Tableau -->
          <div class="overflow-x-auto border rounded-lg">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Variante</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix</th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" v-for="wh in displayWarehouses" :key="wh._id">
                    <img class="flag-img inline mr-1" :src="$f.getCountryCode(wh.country)" />
                    {{ wh.name }}
                  </th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr v-for="variant in filteredVariants" :key="variant.id"
                    :class="{'bg-red-50': getVariantTotalStock(variant) === 0, 'bg-yellow-50': getVariantTotalStock(variant) > 0 && getVariantTotalStock(variant) < 5}">
                  <td class="px-4 py-3">
                    <div class="flex items-center">
                      <img v-if="variant.picture" :src="variant.picture" class="w-10 h-10 rounded mr-2 object-cover" />
                      <img v-else-if="product.picture" :src="product.picture" class="w-10 h-10 rounded mr-2 object-cover opacity-50" />
                      <div>
                        <div class="text-sm font-medium text-gray-900">
                          {{ formatVariantLabel(variant.attributes) }}
                        </div>
                        <div v-if="!variant.isActive" class="text-xs text-red-500">Inactive</div>
                      </div>
                    </div>
                  </td>
                  <td class="px-4 py-3">
                    <span class="text-sm text-gray-600">{{ variant.sku || '-' }}</span>
                  </td>
                  <td class="px-4 py-3">
                    <span class="text-sm text-gray-900">{{ variant.price || product.price }} {{ currentCurrency }}</span>
                  </td>
                  <td class="px-4 py-3 text-center" v-for="wh in displayWarehouses" :key="wh._id">
                    <div class="flex flex-col items-center">
                      <span class="text-sm font-medium" :class="getStockClass(getWarehouseStock(variant, wh._id))">
                        {{ getWarehouseStock(variant, wh._id) }}
                      </span>
                      <button
                        @click="openAdjustModal(variant, wh)"
                        class="text-xs text-blue-500 hover:underline mt-1"
                      >
                        Ajuster
                      </button>
                    </div>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <span class="text-sm font-bold" :class="getStockClass(getVariantTotalStock(variant))">
                      {{ getVariantTotalStock(variant) }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <div class="flex justify-center space-x-2">
                      <button @click="viewStockHistory(variant)" class="p-1 text-blue-500 hover:text-blue-700" title="Historique">
                        <i class="material-icons text-sm">history</i>
                      </button>
                      <button @click="openBulkAdjustModal(variant)" class="p-1 text-green-500 hover:text-green-700" title="Ajustement rapide">
                        <i class="material-icons text-sm">add_circle</i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal d'ajustement de stock -->
    <vue-modal-2 name="adjustStockModal" :headerOptions="{title: 'Ajuster le stock'}" :noFooter="true" modalSize="sm" @on-close="closeAdjustModal">
      <div class="p-4">
        <div v-if="selectedVariant && selectedWarehouse" class="space-y-4">
          <div class="bg-gray-100 p-3 rounded">
            <p class="text-sm text-gray-600">Variante: <strong>{{ formatVariantLabel(selectedVariant.attributes) }}</strong></p>
            <p class="text-sm text-gray-600">Entrepot: <strong>{{ selectedWarehouse.name }}</strong></p>
            <p class="text-sm text-gray-600">Stock actuel: <strong>{{ getWarehouseStock(selectedVariant, selectedWarehouse._id) }}</strong></p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Operation</label>
            <select v-model="adjustment.operation" class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green-500">
              <option value="Addition">Ajouter du stock</option>
              <option value="Subtraction">Retirer du stock</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Type de stock</label>
            <select v-model="adjustment.stockType" class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green-500">
              <option value="inStock-total">En stock + Total</option>
              <option value="inStock">En stock uniquement</option>
              <option value="total">Total uniquement</option>
              <option value="defective">Defectueux</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Quantite</label>
            <input v-model.number="adjustment.quantity" type="number" min="1" class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Raison *</label>
            <input v-model="adjustment.reason" type="text" placeholder="Ex: Reception livraison, Inventaire..." class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green-500" />
          </div>

          <div class="flex space-x-3 pt-4">
            <button @click="closeAdjustModal" class="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              Annuler
            </button>
            <button
              @click="submitAdjustment"
              :disabled="!adjustment.reason || adjustment.quantity < 1"
              class="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Appliquer
            </button>
          </div>
        </div>
      </div>
    </vue-modal-2>

    <!-- Modal d'historique de stock -->
    <vue-modal-2 name="historyModal" :headerOptions="{title: 'Historique du stock'}" :noFooter="true" modalSize="lg" @on-close="closeHistoryModal">
      <div class="p-4">
        <div v-if="selectedVariant" class="mb-4 bg-gray-100 p-3 rounded">
          <p class="text-sm text-gray-600">Variante: <strong>{{ formatVariantLabel(selectedVariant.attributes) }}</strong></p>
        </div>

        <div v-if="stockHistory.length === 0" class="text-center py-8 text-gray-500">
          Aucun historique disponible
        </div>

        <div v-else class="overflow-y-auto max-h-96">
          <table class="w-full">
            <thead class="bg-gray-50 sticky top-0">
              <tr>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500">Entrepot</th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500">Operation</th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500">Quantite</th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500">Raison</th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500">Utilisateur</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-for="history in stockHistory" :key="history._id">
                <td class="px-3 py-2 text-xs text-gray-600">{{ formatDate(history.date) }}</td>
                <td class="px-3 py-2 text-xs text-gray-600">{{ history.warehouse ? history.warehouse.name : history.country }}</td>
                <td class="px-3 py-2">
                  <span class="px-2 py-1 text-xs rounded-full" :class="history.status === 'in' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                    {{ history.status === 'in' ? '+' : '-' }} {{ history.type }}
                  </span>
                </td>
                <td class="px-3 py-2 text-xs font-medium" :class="history.status === 'in' ? 'text-green-600' : 'text-red-600'">
                  {{ history.status === 'in' ? '+' : '-' }}{{ history.quantity }}
                </td>
                <td class="px-3 py-2 text-xs text-gray-600">{{ history.reason }}</td>
                <td class="px-3 py-2 text-xs text-gray-600">{{ history.user ? history.user.email : '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </vue-modal-2>
  </div>
</template>

<script>
export default {
  name: 'ProductVariants',
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object }
  },
  data() {
    return {
      loading: false,
      product: {},
      warehouses: [],
      searchQuery: '',
      warehouseFilter: null,
      stockFilter: '',
      selectedVariant: null,
      selectedWarehouse: null,
      stockHistory: [],
      adjustment: {
        operation: 'Addition',
        stockType: 'inStock-total',
        quantity: 1,
        reason: ''
      }
    }
  },
  computed: {
    currentCurrency() {
      return this.idWharhouse ? this.idWharhouse.currency : 'MAD';
    },
    displayWarehouses() {
      if (this.warehouseFilter) {
        return [this.warehouseFilter];
      }
      return this.warehouses.filter(w => w.is_primary);
    },
    totalVariants() {
      return this.product.variants ? this.product.variants.length : 0;
    },
    totalInStock() {
      if (!this.product.variants) return 0;
      return this.product.variants.reduce((sum, v) => sum + (v.quantity ? v.quantity.inStock : 0), 0);
    },
    lowStockCount() {
      if (!this.product.variants) return 0;
      return this.product.variants.filter(v => {
        const stock = v.quantity ? v.quantity.inStock : 0;
        return stock > 0 && stock < 5;
      }).length;
    },
    outOfStockCount() {
      if (!this.product.variants) return 0;
      return this.product.variants.filter(v => !v.quantity || v.quantity.inStock === 0).length;
    },
    filteredVariants() {
      if (!this.product.variants) return [];

      let variants = [...this.product.variants];

      // Filtre par recherche
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        variants = variants.filter(v => {
          const skuMatch = v.sku && v.sku.toLowerCase().includes(query);
          const attrMatch = v.attributes.some(a =>
            a.value.toLowerCase().includes(query) ||
            (a.label && a.label.toLowerCase().includes(query))
          );
          return skuMatch || attrMatch;
        });
      }

      // Filtre par stock
      if (this.stockFilter) {
        variants = variants.filter(v => {
          const stock = this.getVariantTotalStock(v);
          if (this.stockFilter === 'inStock') return stock > 5;
          if (this.stockFilter === 'lowStock') return stock > 0 && stock <= 5;
          if (this.stockFilter === 'outOfStock') return stock === 0;
          return true;
        });
      }

      return variants;
    }
  },
  async mounted() {
    await this.loadProduct();
    await this.loadWarehouses();
  },
  watch: {
    'idWharhouse': async function() {
      await this.loadProduct();
    }
  },
  methods: {
    async loadProduct() {
      this.loading = true;
      try {
        const res = await this.$server.get('products', { id: this.$route.params.id });
        if (res && res.content) {
          this.product = res.content;
        }
      } catch (error) {
        console.error('Error loading product:', error);
        this.$toast.error('Erreur lors du chargement du produit');
      } finally {
        this.loading = false;
      }
    },
    async loadWarehouses() {
      try {
        const res = await this.$server.search('warehouses', { limit: 100, isDeleted: { $eq: null } });
        if (res && res.content && res.content.results) {
          this.warehouses = res.content.results;
        }
      } catch (error) {
        console.error('Error loading warehouses:', error);
      }
    },
    formatVariantLabel(attributes) {
      if (!attributes || attributes.length === 0) return '';
      return attributes.map(attr => `${attr.attributeName || attr.attributeCode}: ${attr.label || attr.value}`).join(', ');
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    getWarehouseStock(variant, warehouseId) {
      if (!variant.warehouseStocks) return 0;
      const ws = variant.warehouseStocks.find(s =>
        s.warehouse && (s.warehouse._id === warehouseId || s.warehouse.toString() === warehouseId.toString())
      );
      return ws ? ws.quantity.inStock : 0;
    },
    getVariantTotalStock(variant) {
      return variant.quantity ? variant.quantity.inStock : 0;
    },
    getStockClass(stock) {
      if (stock === 0) return 'text-red-600';
      if (stock < 5) return 'text-yellow-600';
      return 'text-green-600';
    },
    filterByWarehouse() {
      // Le filtre est applique via le computed
    },
    openAdjustModal(variant, warehouse) {
      this.selectedVariant = variant;
      this.selectedWarehouse = warehouse;
      this.adjustment = {
        operation: 'Addition',
        stockType: 'inStock-total',
        quantity: 1,
        reason: ''
      };
      this.$vm2.open('adjustStockModal');
    },
    closeAdjustModal() {
      this.$vm2.close('adjustStockModal');
      this.selectedVariant = null;
      this.selectedWarehouse = null;
    },
    openBulkAdjustModal(variant) {
      if (this.warehouses.length > 0) {
        this.openAdjustModal(variant, this.warehouses.find(w => w.is_primary) || this.warehouses[0]);
      }
    },
    async submitAdjustment() {
      if (!this.adjustment.reason || this.adjustment.quantity < 1) {
        this.$toast.warning('Veuillez remplir tous les champs');
        return;
      }

      this.loading = true;
      try {
        const res = await this.$server.update('products', {
          _id: this.product._id,
          updateVariantStock: true,
          variantId: this.selectedVariant.id,
          warehouse: this.selectedWarehouse,
          country: this.selectedWarehouse.country,
          stockType: this.adjustment.stockType,
          operation: this.adjustment.operation,
          quantity: this.adjustment.quantity,
          reason: this.adjustment.reason
        });

        if (res && res.content) {
          this.$toast.success('Stock ajuste avec succes');
          await this.loadProduct();
          this.closeAdjustModal();
        } else {
          this.$toast.error(res.content || 'Erreur lors de l\'ajustement');
        }
      } catch (error) {
        console.error('Error adjusting stock:', error);
        this.$toast.error('Erreur lors de l\'ajustement du stock');
      } finally {
        this.loading = false;
      }
    },
    async viewStockHistory(variant) {
      this.selectedVariant = variant;
      this.stockHistory = [];

      try {
        const res = await this.$server.search('stockHistories', {
          product: this.product._id,
          variantId: variant.id,
          limit: 50
        });

        if (res && res.content && res.content.results) {
          this.stockHistory = res.content.results;
        }
      } catch (error) {
        console.error('Error loading stock history:', error);
      }

      this.$vm2.open('historyModal');
    },
    closeHistoryModal() {
      this.$vm2.close('historyModal');
      this.stockHistory = [];
    }
  }
}
</script>

<style scoped>
.flag-img {
  width: 20px;
  height: 14px;
  vertical-align: middle;
}
</style>
